import React, { useState, useEffect, useCallback } from 'react';
import SquadFormModal from './SquadFormModal';
import IndividualSquadCard from './IndividualSquadCard';
import CreateSquadCard from './CreateSquadCard'; // Import the new component
// No longer need ComposeIcon if not used elsewhere
// import { ComposeIcon } from '../components/Dashbaord/chat/components/ui/inputs/ChatIcons';
import fetchApi from '../components/Lib/api';

const SquadsDashboard = () => {
  const [squads, setSquads] = useState([]);
  const [showSquadModal, setShowSquadModal] = useState(false);
  const [modalInitialSquadData, setModalInitialSquadData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to determine current user ID
  const getCurrentUserId = useCallback(() => {
    // Replace this with your actual authentication logic
    // Example: return authContext.user?.id || localStorage.getItem('userId') || 1;
    return 1; // Placeholder - replace with actual implementation
  }, []);

  const fetchSquads = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchApi('/squads', 'GET');
      if (!response.ok) {
        throw new Error(`Failed to fetch squads: ${response.statusText}`);
      }
      const data = await response.json();
      console.log(data)
      setSquads(Array.isArray(data.owned_squads) ? data.owned_squads : []);
    } catch (err) {
      console.error("Error fetching squads:", err);
      setError(err.message || "Failed to load squads.");
      setSquads([]); // Ensure squads is always an array
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSquads();
  }, [fetchSquads]);

  const handleOpenCreateSquadModal = useCallback(() => {
    setModalInitialSquadData(null);
    setError(null); // Clear any previous errors
    setShowSquadModal(true);
  }, []);

  const handleOpenEditSquadModal = useCallback((squadToEdit) => {
    setModalInitialSquadData(squadToEdit);
    setError(null); // Clear any previous errors
    setShowSquadModal(true);
  }, []);

  const handleCloseSquadModal = useCallback(() => {
    setShowSquadModal(false);
    setModalInitialSquadData(null);
    setError(null);
  }, []);

  const handleSaveSquad = useCallback(async (squadDetails, squadId = null) => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (squadId) {
        response = await fetchApi(`/squads/${squadId}`, 'PATCH', squadDetails);
      } else {
        response = await fetchApi('/squads', 'POST', squadDetails);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${squadId ? 'update' : 'create'} squad.`);
      }

      // Close modal and refresh squads list
      handleCloseSquadModal();
      await fetchSquads();
    } catch (err) {
      console.error("Error saving squad:", err);
      setError(err.message || "An unexpected error occurred during save.");
    } finally {
      setIsLoading(false);
    }
  }, [handleCloseSquadModal, fetchSquads]);

  const handleDeleteSquad = useCallback(async (squadId) => {
    // Prevent multiple simultaneous delete operations
    if (isLoading) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchApi(`/squads/${squadId}`, 'DELETE');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to delete squad.");
      }
      await fetchSquads();
    } catch (err) {
      console.error("Error deleting squad:", err);
      setError(err.message || "An unexpected error occurred during delete.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchSquads, isLoading]);

  const handleLeaveSquad = useCallback(async (squadId) => {
    // Prevent multiple simultaneous leave operations
    if (isLoading) return;

    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      setError("User ID not available to leave squad.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchApi(`/squads/${squadId}/remove_member`, 'DELETE', {
        user_id: currentUserId
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to leave squad.");
      }
      await fetchSquads();
    } catch (err) {
      console.error("Error leaving squad:", err);
      setError(err.message || "An unexpected error occurred while leaving squad.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchSquads, getCurrentUserId, isLoading]);

  return (
    <div>
      {/* Moved the create button to be inside the grid */}
      <div>
        {/* Loading State */}
        {isLoading && squads.length === 0 && (
          <p className="text-center text-gray-500">Loading squads...</p>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}

        {/* Squads Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Create Squad Card - always visible */}
          <CreateSquadCard onClick={handleOpenCreateSquadModal} />

          {/* Existing Squad Cards */}
          {squads.length === 0 && !isLoading && !error && (
            <p className="col-span-full text-center text-gray-500">
              No squads found.
            </p>
          )}
          {squads.map((squad) => (
            <IndividualSquadCard
              key={squad.id}
              squad={squad}
              isOwned={squad.owner_id === getCurrentUserId()}
              onEditClick={handleOpenEditSquadModal}
              onDeleteClick={handleDeleteSquad}
              onLeaveClick={handleLeaveSquad}
            />
          ))}
        </div>
      </div>

      {/* Squad Modal */}
      <SquadFormModal
        show={showSquadModal}
        onClose={handleCloseSquadModal}
        initialSquadData={modalInitialSquadData}
        onSaveSquad={handleSaveSquad}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
};

export default SquadsDashboard;